"use client";
import { FC, HTMLProps, forwardRef, useEffect, useState } from "react";

// libs
import classNames from "classnames";

// components
import { Error } from "@/components/common";

// assets
import { MinusSmallIcon, PlusSmallIcon } from "@/components/assets/icons";
import { useTranslations } from "next-intl";

type InputProps = {
  id?: string;
  label?: string;
  name?: string;
  error?: string | boolean;
  value?: string | number;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  onNumberChange: (value: string) => void;
} & HTMLProps<HTMLInputElement>;

const NumberStepper: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label = "",
      value,
      name,
      onNumberChange,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      ...props
    },
    ref,
  ) => {
    const t = useTranslations();
    const inputClasses = classNames(
      "w-full",
      "bg-gray-100",
      "rounded-[2.25rem]",
      "pl-8",
      "pr-[5.5rem]",
      "h-[63px]",
      "border",
      "leading-tight",
      "text-base",
      "font-normal",
      "break-all",
      "focus:outline-none",
      {
        // error
        "border-red-600": !!error,
        // active
        "border-transparent active:border-[#cfe0e2]": !error,
        // disabled
        "disabled:bg-zinc-100 disabled:text-zinc-400 disabled:border-none":
          !error,
        // success
        "border-emerald-600": success,
      },
      className,
    );

    const [numberValue, setNumberValue] = useState<string>();
    useEffect(() => {
      setNumberValue(value?.toString());
    }, [value]);

    const increaseValue = (value: string) => {
      const valueNumber = parseInt(value || "0");
      const numberToString = (valueNumber + 1).toString();
      setNumberValue(numberToString);
      onNumberChange(numberToString);
    };
    const decreaseValue = (value: string) => {
      const valueNumber = parseInt(value || "0");

      if (valueNumber <= 1) return;

      const numberToString = (valueNumber - 1).toString();
      setNumberValue(numberToString);
      onNumberChange(numberToString);
    };

    return (
      <div className={classNames("relative w-full", containerClassName)}>
        <div className={classNames("input-wrapper w-full", wrapperClassName)}>
          <input
            id={id}
            ref={ref}
            className={classNames("input-field pt-4", inputClasses)}
            name={name}
            disabled={disabled}
            maxLength={maxLength}
            type="text"
            placeholder=""
            onChange={(e) => {
              const value = e.target.value;
              if (/\D/.test(value)) {
                e.preventDefault();
                return;
              } else {
                setNumberValue(e.target.value);
                onNumberChange(e.target.value);
              }
            }}
            value={numberValue || ""}
            {...props}
          />
          <div className="absolute right-6 top-[calc(50%-10px)] flex gap-[0.875rem]">
            <div className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-white">
              <MinusSmallIcon onClick={() => decreaseValue(numberValue)} />
            </div>
            <div className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-white">
              <PlusSmallIcon onClick={() => increaseValue(numberValue)} />
            </div>
          </div>
          <label
            className={classNames(
              "input-label leading-tight",
              labelClassName,
              !!error && "text-red-600 !opacity-100",
            )}
            htmlFor={id}
          >
            {t(label)}
          </label>
        </div>
        {error && typeof error === "string" && <Error text={error} />}
      </div>
    );
  },
);

NumberStepper.displayName = "NumberStepper";

export { NumberStepper };
